import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Globe, Users, Lock, Search, Check, X, Star, Trash2, UserPlus, Loader2, Clock, ArrowRight, SlidersHorizontal, Sparkles, TrendingUp, Layers, ArrowDownLeft, ArrowUpRight, Plus } from "lucide-react";
import { authApi } from "./authApi";

const PROP_TYPES = ["Appartement", "Maison", "Villa", "Terrain", "Immeuble", "Local commercial", "Autre"];
const COLLECTIONS = ["Habitat", "Prestige"];

const MODES = [
  { key: "national", label: "Réseau OFF MARKETTE National", icon: Globe, desc: "Vos annonces peuvent être diffusées sur les sites de tous les membres du réseau national, et vous affichez les leurs. Réciprocité automatique." },
  { key: "private_network", label: "Réseau privé", icon: Users, desc: "Vous choisissez vos partenaires. Seules les annonces des partenaires mutuellement approuvés sont partagées." },
  { key: "private", label: "Réseau personnel", icon: Lock, desc: "Fonctionnement indépendant. Votre site n'affiche que vos propres annonces, sans partage." },
];

const inp = "w-full bg-white/5 border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B] transition-colors";

const eur = (v) => {
  const n = Number(v);
  if (!n) return "0 €";
  return Math.round(n).toLocaleString("fr-FR") + " €";
};

function Avatar({ m, size = 44 }) {
  const s = { width: size, height: size };
  if (m?.logo) return <img src={m.logo} alt="" style={s} className="rounded-full object-cover bg-white/5 shrink-0" />;
  if (m?.photo) return <img src={m.photo} alt="" style={s} className="rounded-full object-cover bg-white/5 shrink-0" />;
  return <div style={s} className="rounded-full bg-[#C19A6B]/15 flex items-center justify-center text-[#C19A6B] shrink-0"><Users className="w-5 h-5" /></div>;
}

export default function NetworkTab() {
  const [settings, setSettings] = useState(null);
  const [parts, setParts] = useState({ incoming: [], outgoing: [], accepted: [] });
  const [dir, setDir] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState("");
  const [recos, setRecos] = useState([]);
  const [stats, setStats] = useState(null);
  const [groups, setGroups] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [apports, setApports] = useState({ incoming: [], outgoing: [], statuses: [] });
  const [analytics, setAnalytics] = useState(null);
  const [newGroup, setNewGroup] = useState("");

  const loadDir = useCallback(async (query = "") => {
    try { const d = await authApi.netDirectory(query); setDir(d.members || []); } catch (_) {}
  }, []);
  const loadParts = useCallback(async () => {
    try { setParts(await authApi.netPartnerships()); } catch (_) {}
  }, []);
  const loadExtras = useCallback(async () => {
    authApi.netRecommendations().then((d) => setRecos(d.recommendations || [])).catch(() => {});
    authApi.netStats().then(setStats).catch(() => {});
    authApi.netGroups().then((d) => { setGroups(d.groups || []); setAssignments(d.assignments || {}); }).catch(() => {});
    authApi.netApports().then(setApports).catch(() => {});
    authApi.netApportsAnalytics().then(setAnalytics).catch(() => {});
  }, []);

  useEffect(() => {
    authApi.netSettings().then(setSettings).catch(() => setSettings({ network_mode: "private", share_settings: { collections: COLLECTIONS, excluded_types: [], only_new: false, partners_limit: [] } }));
    loadParts();
    loadDir();
    loadExtras();
  }, [loadParts, loadDir, loadExtras]);

  useEffect(() => {
    const t = setTimeout(() => loadDir(q), 350);
    return () => clearTimeout(t);
  }, [q, loadDir]);

  if (!settings) return <div className="py-12 text-center text-sm text-[#C19A6B] uppercase tracking-[0.25em] animate-pulse">Chargement…</div>;

  const mode = settings.network_mode;
  const share = settings.share_settings;
  const setMode = (m) => setSettings((s) => ({ ...s, network_mode: m }));
  const setShare = (patch) => setSettings((s) => ({ ...s, share_settings: { ...s.share_settings, ...patch } }));
  const toggleArr = (key, val) => {
    const arr = share[key] || [];
    setShare({ [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] });
  };

  const save = async () => {
    setSaving(true);
    try { const r = await authApi.netSaveSettings({ network_mode: mode, share_settings: share }); setSettings(r); setSaved(true); setTimeout(() => setSaved(false), 2000); } catch (_) { alert("Enregistrement impossible."); }
    setSaving(false);
  };

  const doRequest = async (id) => { setBusy(id); try { await authApi.netRequest(id); await Promise.all([loadDir(q), loadParts(), loadExtras()]); } catch (_) {} setBusy(""); };
  const doAccept = async (id) => { setBusy(id); try { await authApi.netAccept(id); await Promise.all([loadDir(q), loadParts(), loadExtras()]); } catch (_) {} setBusy(""); };
  const doReject = async (id) => { setBusy(id); try { await authApi.netReject(id); await Promise.all([loadDir(q), loadParts()]); } catch (_) {} setBusy(""); };
  const doTerminate = async (id) => { if (!window.confirm("Mettre fin à ce partenariat ?")) return; setBusy(id); try { await authApi.netTerminate(id); await Promise.all([loadDir(q), loadParts(), loadExtras()]); } catch (_) {} setBusy(""); };
  const doFav = async (mid) => { try { await authApi.netToggleFav(mid); await Promise.all([loadDir(q), loadParts()]); } catch (_) {} };
  const addGroup = async () => { const g = newGroup.trim(); if (!g || groups.includes(g)) return; const ng = [...groups, g]; setGroups(ng); setNewGroup(""); try { await authApi.netSaveGroups(ng); } catch (_) {} };
  const removeGroup = async (g) => { const ng = groups.filter((x) => x !== g); setGroups(ng); try { await authApi.netSaveGroups(ng); } catch (_) {} };
  const toggleAssign = async (mid, g) => {
    const cur = assignments[mid] || [];
    const next = cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g];
    setAssignments((a) => ({ ...a, [mid]: next }));
    try { await authApi.netAssignGroups(mid, next); } catch (_) {}
  };
  const setApportStatus = async (id, status) => {
    setApports((a) => ({ ...a, incoming: a.incoming.map((x) => x.id === id ? { ...x, status } : x), outgoing: a.outgoing.map((x) => x.id === id ? { ...x, status } : x) }));
    try { await authApi.netUpdateApport(id, status); authApi.netApports().then(setApports).catch(() => {}); authApi.netApportsAnalytics().then(setAnalytics).catch(() => {}); } catch (_) {}
  };
  const markApportPaid = async (id) => {
    try { await authApi.netApportPayout(id, "paid"); authApi.netApports().then(setApports).catch(() => {}); authApi.netApportsAnalytics().then(setAnalytics).catch(() => {}); } catch (_) {}
  };

  const relLabel = { pending_out: "Demande envoyée", pending_in: "Vous a sollicité", accepted: "Partenaire" };

  return (
    <div className="space-y-8" data-testid="network-tab">
      {/* Stats de collaboration */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="net-stats">
          {[
            { label: "Partenaires actifs", value: stats.partners_count, icon: Users },
            { label: "Annonces partenaires sur mon site", value: stats.partner_listings_on_my_site, icon: Layers },
            { label: "Apports reçus", value: stats.apports_in, icon: ArrowDownLeft },
            { label: "Apports transmis", value: stats.apports_out, icon: ArrowUpRight },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="border border-white/10 p-4">
                <Icon className="w-4 h-4 text-[#C19A6B] mb-2" />
                <div className="text-2xl font-bold text-white">{s.value}</div>
                <div className="text-[11px] text-zinc-500 leading-tight mt-1">{s.label}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recommandations de partenaires */}
      {recos.length > 0 && (
        <div className="border border-white/10 p-5" data-testid="net-recommendations">
          <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] mb-1 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#C19A6B]" /> Partenaires suggérés</p>
          <p className="text-xs text-zinc-500 mb-4">Professionnels pertinents selon votre localisation et votre spécialité.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {recos.map((r) => (
              <div key={r.member_id} className="border border-white/10 p-3 flex flex-col gap-2" data-testid={`net-reco-${r.member_id}`}>
                <div className="flex items-center gap-2">
                  <Avatar m={r} size={38} />
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{r.display_name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{r.account_type} · {r.city}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(r.match_reasons || []).slice(0, 2).map((x) => <span key={x} className="text-[10px] px-2 py-0.5 bg-white/5 text-zinc-400 rounded-full">{x}</span>)}
                </div>
                <button onClick={() => doRequest(r.member_id)} disabled={busy === r.member_id} data-testid={`net-reco-request-${r.member_id}`} className="mt-1 inline-flex items-center justify-center gap-1 text-[11px] uppercase tracking-wider border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 px-3 py-1.5 disabled:opacity-50">{busy === r.member_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} Partenariat</button>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Mode réseau */}
      <div className="border border-white/10 p-5">
        <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] mb-1 flex items-center gap-2"><Globe className="w-5 h-5 text-[#C19A6B]" /> Mon niveau d'appartenance au réseau</p>
        <p className="text-xs text-zinc-500 mb-4">Choisissez comment vous participez au réseau OFF MARKETTE.</p>
        <div className="grid md:grid-cols-3 gap-3">
          {MODES.map((mo) => {
            const active = mode === mo.key;
            const Icon = mo.icon;
            return (
              <button key={mo.key} onClick={() => setMode(mo.key)} data-testid={`net-mode-${mo.key}`} className={`text-left p-4 border transition-colors ${active ? "border-[#C19A6B] bg-[#C19A6B]/10" : "border-white/10 hover:border-white/30"}`}>
                <Icon className={`w-5 h-5 mb-2 ${active ? "text-[#C19A6B]" : "text-zinc-400"}`} />
                <div className={`text-sm font-medium mb-1 ${active ? "text-white" : "text-zinc-200"}`}>{mo.label}</div>
                <div className="text-[11px] text-zinc-500 leading-relaxed">{mo.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Paramètres de diffusion */}
      {mode !== "private" && (
        <div className="border border-white/10 p-5 space-y-4" data-testid="net-share-settings">
          <p className="text-sm uppercase tracking-wider text-zinc-300 flex items-center gap-2"><SlidersHorizontal className="w-4 h-4 text-[#C19A6B]" /> Paramètres de diffusion</p>
          <p className="text-xs text-zinc-500">Choisissez précisément quelles annonces vous partagez avec le réseau/vos partenaires.</p>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Collections partagées</label>
            <div className="flex gap-2 flex-wrap">
              {COLLECTIONS.map((c) => (
                <button key={c} onClick={() => toggleArr("collections", c)} data-testid={`net-col-${c}`} className={`text-xs uppercase tracking-wider px-3 py-1.5 border ${(share.collections || []).includes(c) ? "border-[#C19A6B] text-[#C19A6B] bg-[#C19A6B]/10" : "border-white/10 text-zinc-400"}`}>{c}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Types de biens exclus du partage</label>
            <div className="flex gap-2 flex-wrap">
              {PROP_TYPES.map((t) => (
                <button key={t} onClick={() => toggleArr("excluded_types", t)} data-testid={`net-excl-${t}`} className={`text-xs px-3 py-1.5 border ${(share.excluded_types || []).includes(t) ? "border-red-400/60 text-red-300 bg-red-500/10" : "border-white/10 text-zinc-400"}`}>{t}</button>
              ))}
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-zinc-300 cursor-pointer" data-testid="net-only-new">
            <input type="checkbox" checked={!!share.only_new} onChange={(e) => setShare({ only_new: e.target.checked })} className="accent-[#C19A6B]" />
            Partager uniquement mes nouveautés (moins de 30 jours)
          </label>
          {mode === "private_network" && parts.accepted.length > 0 && (
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Limiter le partage à certains partenaires <span className="text-zinc-600">(vide = tous)</span></label>
              <div className="flex gap-2 flex-wrap">
                {parts.accepted.map((p) => (
                  <button key={p.member.member_id} onClick={() => toggleArr("partners_limit", p.member.member_id)} className={`text-xs px-3 py-1.5 border ${(share.partners_limit || []).includes(p.member.member_id) ? "border-[#C19A6B] text-[#C19A6B] bg-[#C19A6B]/10" : "border-white/10 text-zinc-400"}`}>{p.member.display_name}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="net-save" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">{saved ? "Enregistré ✓" : saving ? "…" : "Enregistrer mon réseau"} <Check className="w-4 h-4" /></button>
      </div>

      {/* Demandes reçues */}
      {parts.incoming.length > 0 && (
        <div className="border border-[#C19A6B]/30 bg-[#C19A6B]/5 p-5" data-testid="net-incoming">
          <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] mb-3 flex items-center gap-2"><UserPlus className="w-5 h-5" /> Demandes de partenariat reçues ({parts.incoming.length})</p>
          <div className="space-y-2">
            {parts.incoming.map((p) => (
              <div key={p.id} className="flex items-center gap-3 border border-white/10 bg-[#0d0d10] px-3 py-2.5" data-testid={`net-incoming-${p.member.member_id}`}>
                <Avatar m={p.member} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{p.member.display_name}</div>
                  <div className="text-xs text-zinc-500 truncate">{p.member.company} · {p.member.city}</div>
                </div>
                <button onClick={() => doAccept(p.id)} disabled={busy === p.id} data-testid={`net-accept-${p.member.member_id}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider bg-[#C19A6B] text-white px-3 py-1.5 disabled:opacity-50">{busy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Accepter</button>
                <button onClick={() => doReject(p.id)} disabled={busy === p.id} data-testid={`net-reject-${p.member.member_id}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider border border-white/15 text-zinc-300 px-3 py-1.5 hover:border-red-400 hover:text-red-400"><X className="w-3.5 h-3.5" /> Refuser</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mes partenaires */}
      {parts.accepted.length > 0 && (
        <div className="border border-white/10 p-5" data-testid="net-partners">
          <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-[#C19A6B]" /> Mes partenaires ({parts.accepted.length})</p>
          {/* Gestion des groupes */}
          <div className="mb-4 pb-4 border-b border-white/10" data-testid="net-groups">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <Layers className="w-3.5 h-3.5 text-[#C19A6B]" />
              <span className="text-[11px] uppercase tracking-wider text-zinc-500">Groupes</span>
              {groups.map((g) => (
                <span key={g} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-white/5 text-zinc-300 rounded-full">{g}<button onClick={() => removeGroup(g)} className="text-zinc-500 hover:text-red-400"><X className="w-3 h-3" /></button></span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={newGroup} onChange={(e) => setNewGroup(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addGroup()} placeholder="Nouveau groupe (ex : Oise, Prestige, Notaires)" data-testid="net-group-input" className={`${inp} flex-1`} />
              <button onClick={addGroup} data-testid="net-group-add" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider border border-white/15 text-zinc-300 px-3 hover:border-[#C19A6B] hover:text-[#C19A6B]"><Plus className="w-3.5 h-3.5" /> Ajouter</button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {parts.accepted.map((p) => (
              <div key={p.id} className="border border-white/10 px-3 py-2.5 space-y-2" data-testid={`net-partner-${p.member.member_id}`}>
                <div className="flex items-center gap-3">
                  <Avatar m={p.member} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{p.member.display_name}</div>
                    <div className="text-xs text-zinc-500 truncate">{p.member.company} · {p.member.city}</div>
                  </div>
                  <button onClick={() => doFav(p.member.member_id)} title="Favori" data-testid={`net-fav-${p.member.member_id}`} className={`p-1.5 ${p.is_favorite ? "text-[#C19A6B]" : "text-zinc-600 hover:text-[#C19A6B]"}`}><Star className={`w-4 h-4 ${p.is_favorite ? "fill-[#C19A6B]" : ""}`} /></button>
                  <button onClick={() => doTerminate(p.id)} disabled={busy === p.id} title="Mettre fin" data-testid={`net-terminate-${p.member.member_id}`} className="p-1.5 text-zinc-600 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
                {groups.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {groups.map((g) => {
                      const on = (assignments[p.member.member_id] || []).includes(g);
                      return <button key={g} onClick={() => toggleAssign(p.member.member_id, g)} className={`text-[10px] px-2 py-0.5 rounded-full border ${on ? "border-[#C19A6B] text-[#C19A6B] bg-[#C19A6B]/10" : "border-white/10 text-zinc-500"}`}>{g}</button>;
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Apports d'affaires */}
      {(apports.incoming.length > 0 || apports.outgoing.length > 0) && (
        <div className="border border-white/10 p-5 space-y-4" data-testid="net-apports">
          <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#C19A6B]" /> Apports d'affaires</p>

          {/* Analytics : taux de transformation + classement */}
          {analytics && (analytics.received.total > 0 || analytics.given.total > 0) && (
            <div className="grid md:grid-cols-2 gap-3" data-testid="net-apports-analytics">
              <div className="border border-white/10 p-4">
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Transformation (apports reçus)</p>
                <div className="flex items-end gap-3 mb-3">
                  <span className="text-3xl font-bold text-[#C19A6B]" data-testid="conversion-rate">{analytics.received.conversion_rate}%</span>
                  <span className="text-xs text-zinc-500 mb-1">{analytics.received.won}/{analytics.received.total} vendu(s)</span>
                </div>
                <div className="space-y-1.5">
                  {["contact", "visite", "offre", "vendu", "perdu"].map((s) => {
                    const v = analytics.received.by_status[s] || 0;
                    const pct = analytics.received.total ? Math.round(100 * v / analytics.received.total) : 0;
                    return (
                      <div key={s} className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 w-14">{s}</span>
                        <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden"><div className={`h-full rounded-full ${s === "vendu" ? "bg-emerald-400" : s === "perdu" ? "bg-red-400/60" : "bg-[#C19A6B]"}`} style={{ width: `${pct}%` }} /></div>
                        <span className="text-[10px] text-zinc-400 w-5 text-right">{v}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="border border-white/10 p-4">
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#C19A6B]" /> Meilleurs apporteurs</p>
                {analytics.top_apporteurs.length === 0 ? (
                  <p className="text-xs text-zinc-600">Aucun apport reçu pour le moment.</p>
                ) : (
                  <div className="space-y-2" data-testid="net-leaderboard">
                    {analytics.top_apporteurs.map((t, idx) => (
                      <div key={t.member.member_id} className="flex items-center gap-3" data-testid={`leaderboard-${t.member.member_id}`}>
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-medium ${idx === 0 ? "bg-[#C19A6B] text-white" : "bg-white/5 text-zinc-400"}`}>{idx + 1}</span>
                        <Avatar m={t.member} size={30} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white truncate">{t.member.display_name}</div>
                          <div className="text-[10px] text-zinc-500">{t.total} apport(s) · {t.won} vendu(s) · {t.conversion_rate}%</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {analytics && (analytics.earnings || analytics.payouts_due) && ((analytics.earnings?.total || 0) > 0 || (analytics.payouts_due?.pending || 0) > 0 || (analytics.payouts_due?.paid || 0) > 0) && (
            <div className="grid sm:grid-cols-3 gap-3" data-testid="net-apport-earnings">
              <div className="border border-[#C19A6B]/30 bg-[#C19A6B]/10 p-4">
                <p className="text-[11px] uppercase tracking-wider text-zinc-400">Mes commissions d'apport (8%)</p>
                <p className="text-2xl font-light text-[#C19A6B] mt-1" data-testid="apport-earnings-total">{eur(analytics.earnings?.total)}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">dont {eur(analytics.earnings?.pending)} à recevoir</p>
              </div>
              <div className="border border-white/10 p-4">
                <p className="text-[11px] uppercase tracking-wider text-zinc-400">À reverser aux apporteurs</p>
                <p className="text-2xl font-light text-white mt-1" data-testid="apport-payouts-due">{eur(analytics.payouts_due?.pending)}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">déjà payé : {eur(analytics.payouts_due?.paid)}</p>
              </div>
              <div className="border border-white/10 p-4">
                <p className="text-[11px] uppercase tracking-wider text-zinc-400">Barème apporteur d'acquéreur</p>
                <p className="text-sm text-zinc-300 mt-2">Apporteur <b className="text-[#C19A6B]">{analytics.introducer_pct || 8}%</b> · Plateforme {analytics.platform_pct || 2}%</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">sur la commission d'agence · titulaire 90%</p>
              </div>
            </div>
          )}
          {apports.incoming.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1"><ArrowDownLeft className="w-3.5 h-3.5" /> Reçus (contacts qu'on m'apporte)</p>
              <div className="space-y-2">
                {apports.incoming.map((a) => (
                  <div key={a.id} className="border border-white/10 px-3 py-2.5 flex items-center gap-3 flex-wrap" data-testid={`net-apport-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{a.listing_title}</div>
                      <div className="text-xs text-zinc-500 truncate">via {a.member?.display_name} · {a.lead?.name} ({a.lead?.email})</div>
                      {a.commission && a.commission.governed_by === "apporteur" && (
                        <div className="text-[11px] mt-1 text-zinc-300" data-testid={`net-apport-comm-${a.id}`}>
                          Commission apporteur : <b className="text-[#C19A6B]">{eur(a.commission.apporteur_amount)}</b> (8%) · plateforme {eur(a.commission.platform_amount)} (2%)
                          {a.commission.payout_status === "paid"
                            ? <span className="ml-2 text-emerald-400 uppercase tracking-wider text-[10px]">Payée</span>
                            : <button onClick={() => markApportPaid(a.id)} data-testid={`net-apport-pay-${a.id}`} className="ml-2 text-[10px] uppercase tracking-wider border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 px-2 py-0.5">Marquer payée</button>}
                        </div>
                      )}
                      {a.commission && a.commission.governed_by === "comandat" && (
                        <div className="text-[11px] mt-1 text-zinc-500" data-testid={`net-apport-comm-${a.id}`}>Rémunéré via co-mandat (prioritaire).</div>
                      )}
                    </div>
                    <select value={a.status} onChange={(e) => setApportStatus(a.id, e.target.value)} data-testid={`net-apport-status-${a.id}`} className="bg-white/5 border border-white/15 text-xs text-white px-2 py-1.5 outline-none focus:border-[#C19A6B]">
                      {apports.statuses.map((s) => <option key={s} value={s} className="bg-[#0d0d10]">{s}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
          {apports.outgoing.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1"><ArrowUpRight className="w-3.5 h-3.5" /> Transmis (contacts que j'apporte)</p>
              <div className="space-y-2">
                {apports.outgoing.map((a) => (
                  <div key={a.id} className="border border-white/10 px-3 py-2.5 flex items-center gap-3" data-testid={`net-apport-out-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{a.listing_title}</div>
                      <div className="text-xs text-zinc-500 truncate">pour {a.member?.display_name} · {a.lead?.name}</div>
                      {a.commission && a.commission.governed_by === "apporteur" && (
                        <div className="text-[11px] mt-1" data-testid={`net-apport-out-comm-${a.id}`}>
                          <span className="text-emerald-400">Commission d'apport : <b>{eur(a.commission.apporteur_amount)}</b></span>
                          <span className="text-zinc-500"> · {a.commission.payout_status === "paid" ? "reçue" : "à recevoir"}</span>
                        </div>
                      )}
                    </div>
                    <span className="text-xs uppercase tracking-wider text-[#C19A6B]">{a.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Annuaire / recherche */}
      <div className="border border-white/10 p-5" data-testid="net-directory">
        <p className="text-sm uppercase tracking-wider text-zinc-300 mb-3 flex items-center gap-2"><Search className="w-4 h-4 text-[#C19A6B]" /> Rechercher un professionnel</p>
        <div className="relative mb-4">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, société, ville…" data-testid="net-search" className={`${inp} pl-9`} />
        </div>
        <div className="space-y-2">
          {dir.length === 0 && <p className="text-xs text-zinc-500">Aucun professionnel trouvé.</p>}
          {dir.map((m) => (
            <div key={m.member_id} className="flex items-center gap-3 border border-white/10 px-3 py-2.5" data-testid={`net-dir-${m.member_id}`}>
              <Avatar m={m} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{m.display_name}</div>
                <div className="text-xs text-zinc-500 truncate">{m.account_type} · {m.company} · {m.city}</div>
              </div>
              {m.relationship === "none" && (
                <button onClick={() => doRequest(m.member_id)} disabled={busy === m.member_id} data-testid={`net-request-${m.member_id}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 px-3 py-1.5 disabled:opacity-50">{busy === m.member_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} Demander un partenariat</button>
              )}
              {m.relationship === "pending_out" && <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-zinc-500"><Clock className="w-3.5 h-3.5" /> {relLabel.pending_out}</span>}
              {m.relationship === "pending_in" && (
                <button onClick={() => doAccept(m.partnership_id)} disabled={busy === m.partnership_id} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider bg-[#C19A6B] text-white px-3 py-1.5"><Check className="w-3.5 h-3.5" /> Accepter</button>
              )}
              {m.relationship === "accepted" && <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#C19A6B]"><Check className="w-3.5 h-3.5" /> {relLabel.accepted}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
